import React, { useEffect, useLayoutEffect, useState, useCallback, useMemo, useRef } from "react";
import { Resizable } from "re-resizable";
import { HiOutlineArrowsExpand, HiOutlineMinus } from "react-icons/hi";
import "@livekit/components-styles";
import "./VisualizerOverrides.css";
import "./VideoChatLiveKit.css";
import {
    LiveKitRoom,
    GridLayout,
    TrackLoop,
    ParticipantTile,
    RoomAudioRenderer,
    ControlBar,
    useTracks,
    useParticipants,
    ConnectionStateToast,
    PreJoin,
    usePreviewTracks,
} from "@livekit/components-react";
import type { LocalUserChoices } from "@livekit/components-core";
import { Track, LocalAudioTrack } from "livekit-client";
import { createPortal } from "react-dom";
import PreJoinVisualizer from "./PreJoinVisualizer";
import { VISUALIZER_WIDTH, VISUALIZER_HEIGHT } from "./visualizerConstants";

export interface VideoChatLiveKitProps {
    sessionId: string;
    darkMode?: boolean;
    userName?: string;
    onPresenceUpdate?: (participants: { id: string; name: string }[]) => void;
    activeUsersCount: number;
}

/** Strip hardware IDs like (3A71:F4B5) from device labels for cleaner display. */
function sanitizeDeviceLabel(label: string): string {
    return label.replace(/\s*\([0-9a-fA-F]{4}:[0-9a-fA-F]{4}\)\s*$/g, "").trim();
}

/**
 * Prefer built-in/local camera over remote ones (e.g. iPhone over Continuity when offline).
 * Call after getUserMedia to ensure labels are populated.
 */
async function getPreferredBuiltInVideoDeviceId(): Promise<string | undefined> {
    try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter((d) => d.kind === "videoinput");
        const builtInPatterns = [
            /facetime\s*hd\s*camera/i,
            /built-in/i,
            /integrated\s*camera/i,
            /internal\s*camera/i,
            /macbook.*camera/i,
        ];
        for (const dev of videoDevices) {
            const label = (dev.label || "").toLowerCase();
            if (builtInPatterns.some((p) => p.test(label))) {
                return dev.deviceId;
            }
        }
        return videoDevices[0]?.deviceId;
    } catch {
        return undefined;
    }
}

/**
 * Inner component — renders inside <LiveKitRoom> so hooks work.
 */
const VideoChatInner: React.FC<{
    onPresenceUpdate?: VideoChatLiveKitProps["onPresenceUpdate"];
    isFullscreen: boolean;
    onFullscreenToggle: () => void;
}> = ({ onPresenceUpdate, isFullscreen, onFullscreenToggle }) => {
    const participants = useParticipants();
    const controlRowRef = useRef<HTMLDivElement>(null);
    const tracks = useTracks(
        [
            { source: Track.Source.Camera, withPlaceholder: true },
            { source: Track.Source.ScreenShare, withPlaceholder: false },
        ],
        { onlySubscribed: false }
    );
    const micTracks = useTracks(
        [{ source: Track.Source.Microphone, withPlaceholder: true }],
        { onlySubscribed: false }
    );
    const localMicTrackRef = useMemo(
        () => micTracks.find((t) => t.participant.isLocal),
        [micTracks]
    );

    // Split tracks: screen share above, camera in grid below
    const screenShareTracks = tracks.filter(
        (t) => t.source === Track.Source.ScreenShare
    );
    const cameraTracks = tracks.filter(
        (t) => t.source === Track.Source.Camera
    );

    // Notify parent of presence changes
    useEffect(() => {
        if (onPresenceUpdate) {
            const mapped = participants.map((p) => ({
                id: p.identity,
                name: p.name || p.identity,
            }));
            onPresenceUpdate(mapped);
        }
    }, [participants, onPresenceUpdate]);

    // Note: Device menu deduplication removed - it was mutating DOM (textContent, display)
    // and breaking LiveKit's click handlers, preventing camera/mic selection.

    // 3 or 4 camera participants: custom grid; otherwise use GridLayout
    const useCustomLayout =
        screenShareTracks.length === 0 &&
        (cameraTracks.length === 3 || cameraTracks.length === 4);
    const gridClass =
        useCustomLayout
            ? `videochat-lk-grid videochat-lk-grid-custom videochat-lk-grid-${cameraTracks.length}`
            : "videochat-lk-grid";

    const hasScreenShare = screenShareTracks.length > 0;
    const isSpotlight = isFullscreen && hasScreenShare;

    const screenshareWrapperRef = useRef<HTMLDivElement>(null);
    const [screenshareSize, setScreenshareSize] = useState<{ width: number; height: number } | null>(null);

    useEffect(() => {
        if (!hasScreenShare || isSpotlight || !screenshareWrapperRef.current) return;
        const el = screenshareWrapperRef.current;
        let cancelled = false;
        let timeoutId: ReturnType<typeof setTimeout> | undefined;
        const measure = () => {
            if (cancelled) return;
            const rect = el.getBoundingClientRect();
            const w = Math.round(rect.width);
            const h = Math.round(rect.height);
            if (rect.width > 0 && rect.height > 0) {
                setScreenshareSize({ width: w, height: h });
            }
        };
        /* Double rAF + 50ms delay: wait for layout to fully settle before
         * first measure. Fixes "first drag resistance" where Resizable
         * mounted with stale dimensions. */
        const raf1 = requestAnimationFrame(() => {
            requestAnimationFrame(() => {
                measure();
                timeoutId = setTimeout(measure, 50);
            });
        });
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => {
            cancelled = true;
            cancelAnimationFrame(raf1);
            if (timeoutId != null) clearTimeout(timeoutId);
            ro.disconnect();
        };
    }, [hasScreenShare, isSpotlight]);

    return (
        <>
            <RoomAudioRenderer />
            <ConnectionStateToast />
            {hasScreenShare && !isSpotlight && (
                <div ref={screenshareWrapperRef} className="videochat-lk-screenshare-wrapper">
                    <Resizable
                        className="videochat-lk-screenshare-resizable"
                        defaultSize={
                            screenshareSize ?? {
                                width: typeof window !== "undefined" ? Math.min(800, window.innerWidth * 0.9) : 800,
                                height: typeof window !== "undefined" ? Math.min(450, window.innerHeight * 0.4) : 450,
                            }
                        }
                        minHeight={80}
                        maxHeight={typeof window !== "undefined" ? window.innerHeight * 0.9 : 800}
                        maxWidth={typeof window !== "undefined" ? window.innerWidth * 0.9 : 1200}
                        minWidth={200}
                        bounds="window"
                        enable={{
                            top: true,
                            topLeft: true,
                            topRight: true,
                            bottom: false,
                            left: true,
                            right: true,
                            bottomLeft: false,
                            bottomRight: false,
                        }}
                    >
                        <div className="videochat-lk-screenshare-section">
                            <TrackLoop tracks={screenShareTracks}>
                                <ParticipantTile />
                            </TrackLoop>
                        </div>
                    </Resizable>
                </div>
            )}
            {isSpotlight ? (
                <div className="videochat-lk-spotlight">
                    <div className="videochat-lk-spotlight-main">
                        <TrackLoop tracks={screenShareTracks}>
                            <ParticipantTile />
                        </TrackLoop>
                    </div>
                    <div className="videochat-lk-spotlight-cameras">
                        <TrackLoop tracks={cameraTracks}>
                            <ParticipantTile />
                        </TrackLoop>
                    </div>
                </div>
            ) : (
                <div className={gridClass}>
                    {useCustomLayout ? (
                        <div className="videochat-lk-custom-grid">
                            <TrackLoop tracks={cameraTracks}>
                                <ParticipantTile />
                            </TrackLoop>
                        </div>
                    ) : (
                        <GridLayout tracks={cameraTracks}>
                            <ParticipantTile />
                        </GridLayout>
                    )}
                </div>
            )}
            <div className="videochat-lk-control-row" ref={controlRowRef}>
                <button
                    type="button"
                    className="videochat-lk-fullscreen-btn"
                    onClick={onFullscreenToggle}
                    title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                    aria-label={isFullscreen ? "Exit fullscreen" : "Fullscreen"}
                >
                    {isFullscreen ? (
                        <HiOutlineMinus size={20} />
                    ) : (
                        <HiOutlineArrowsExpand size={20} />
                    )}
                </button>
                <div className="videochat-lk-bar-visualizer">
                    {(localMicTrackRef?.publication?.track as LocalAudioTrack | undefined) ? (
                        <PreJoinVisualizer
                            track={localMicTrackRef!.publication!.track as LocalAudioTrack}
                        />
                    ) : (
                        <div style={{ width: VISUALIZER_WIDTH, height: VISUALIZER_HEIGHT, display: "flex", alignItems: "center", justifyContent: "center" }} aria-hidden />
                    )}
                </div>
                <ControlBar
                    variation="minimal"
                    controls={{
                        microphone: true,
                        camera: true,
                        screenShare: true,
                        chat: false,
                        leave: true,
                        settings: false,
                    }}
                />
            </div>
        </>
    );
};

/**
 * LiveKit-based video chat component.
 * Replaces the old VideoChat (simple-peer + Socket.IO) with LiveKit components
 * that support multiple participants, TURN relay, reconnection, and device management.
 */
const VideoChatLiveKit: React.FC<VideoChatLiveKitProps> = ({
    sessionId,
    darkMode = false,
    userName,
    onPresenceUpdate,
    activeUsersCount,
}) => {
    const [wantsToJoin, setWantsToJoin] = useState(false);
    const [preJoinChoices, setPreJoinChoices] = useState<LocalUserChoices | undefined>(undefined);
    const [preferredVideoDeviceId, setPreferredVideoDeviceId] = useState<string | undefined>(undefined);
    const [deviceDefaultsReady, setDeviceDefaultsReady] = useState(false);
    const [token, setToken] = useState<string | null>(null);
    const [url, setUrl] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const prejoinWrapperRef = useRef<HTMLDivElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [prejoinVisualizerSlot, setPrejoinVisualizerSlot] = useState<HTMLDivElement | null>(null);
    const [prejoinMicEnabled, setPrejoinMicEnabled] = useState(true);

    // Preview tracks for prejoin bar visualizer (matches PreJoin defaults)
    const shouldShowPrejoin = !wantsToJoin && activeUsersCount >= 2 && deviceDefaultsReady;
    const previewTracks = usePreviewTracks(
        shouldShowPrejoin
            ? {
                  audio: true,
                  video: preferredVideoDeviceId
                      ? { deviceId: preferredVideoDeviceId }
                      : true,
              }
            : { audio: false, video: false }
    );
    const prejoinAudioTrack = useMemo(
        () => previewTracks?.find((t) => t.kind === Track.Kind.Audio) as import("livekit-client").LocalAudioTrack | undefined,
        [previewTracks]
    );

    // Prefer built-in camera (FaceTime HD, etc.) over remote (iPhone Continuity) when available
    useEffect(() => {
        if (activeUsersCount < 2) {
            setDeviceDefaultsReady(false);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const stream = await navigator.mediaDevices.getUserMedia({ video: true });
                if (cancelled) {
                    stream.getTracks().forEach((t) => t.stop());
                    return;
                }
                const deviceId = await getPreferredBuiltInVideoDeviceId();
                stream.getTracks().forEach((t) => t.stop());
                if (cancelled) return;
                setPreferredVideoDeviceId(deviceId);
            } catch {
                // Permission denied or no devices - use browser default
            } finally {
                if (!cancelled) setDeviceDefaultsReady(true);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activeUsersCount]);

    // Move Join button into last button-group-container so it stays inline with mic/camera
    const moveJoinIntoControlRow = useCallback(() => {
        const wrapper = prejoinWrapperRef.current;
        if (!wrapper) return;
        const containers = wrapper.querySelectorAll(".lk-button-group-container");
        const container = containers.length > 0 ? containers[containers.length - 1] : null;
        const button = wrapper.querySelector("button.lk-join-button, button[type='submit']");
        if (container && button && !container.contains(button)) {
            container.appendChild(button);
        }
    }, []);

    // Run before paint so Join is inline from first frame when possible
    useLayoutEffect(() => {
        moveJoinIntoControlRow();
    }, [moveJoinIntoControlRow]);

    useEffect(() => {
        moveJoinIntoControlRow();
        // Retry - PreJoin DOM may not be ready yet on first render or when returning from a room
        const raf = requestAnimationFrame(() => moveJoinIntoControlRow());
        const t0 = setTimeout(moveJoinIntoControlRow, 0);
        const t1 = setTimeout(moveJoinIntoControlRow, 50);
        const t2 = setTimeout(moveJoinIntoControlRow, 200);
        const t3 = setTimeout(moveJoinIntoControlRow, 500);
        // Re-run when DOM changes (LiveKit may re-render and move button back)
        const wrapper = prejoinWrapperRef.current;
        const observer =
            wrapper &&
            new MutationObserver(() => {
                const btn = wrapper.querySelector("button.lk-join-button, button[type='submit']");
                const lastContainer = wrapper.querySelector(".lk-button-group-container:last-of-type");
                if (btn && lastContainer && !lastContainer.contains(btn)) {
                    moveJoinIntoControlRow();
                }
            });
        if (observer && wrapper) {
            observer.observe(wrapper, { childList: true, subtree: true });
        }
        return () => {
            cancelAnimationFrame(raf);
            clearTimeout(t0);
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
            observer?.disconnect();
        };
    }, [activeUsersCount, wantsToJoin, moveJoinIntoControlRow]);

    // Inject bar visualizer slot to the left of mic icon in prejoin
    useEffect(() => {
        if (!shouldShowPrejoin || !prejoinWrapperRef.current) {
            setPrejoinVisualizerSlot(null);
            return;
        }
        const inject = () => {
            const container = prejoinWrapperRef.current?.querySelector(".lk-button-group-container");
            if (!container || container.querySelector(".videochat-lk-prejoin-bar-visualizer")) return;
            const slot = document.createElement("div");
            slot.className = "videochat-lk-prejoin-bar-visualizer";
            container.insertBefore(slot, container.firstChild);
            setPrejoinVisualizerSlot(slot);
        };
        inject();
        const t0 = setTimeout(inject, 0);
        const t1 = setTimeout(inject, 100);
        const observer =
            prejoinWrapperRef.current &&
            new MutationObserver(() => {
                if (!prejoinWrapperRef.current?.querySelector(".videochat-lk-prejoin-bar-visualizer")) inject();
            });
        if (observer && prejoinWrapperRef.current) {
            observer.observe(prejoinWrapperRef.current, { childList: true, subtree: true });
        }
        return () => {
            clearTimeout(t0);
            clearTimeout(t1);
            observer?.disconnect();
            prejoinWrapperRef.current?.querySelector(".videochat-lk-prejoin-bar-visualizer")?.remove();
            setPrejoinVisualizerSlot(null);
        };
    }, [activeUsersCount, wantsToJoin, deviceDefaultsReady, shouldShowPrejoin]);

    // Observe PreJoin mic button state so visualizer shows flat bars when mic is off
    useEffect(() => {
        const wrapper = prejoinWrapperRef.current;
        if (!wrapper || !shouldShowPrejoin) return;
        const updateMicEnabled = () => {
            const btn = wrapper.querySelector('button[data-lk-source="microphone"]');
            const enabled = btn?.getAttribute("data-lk-enabled") !== "false";
            setPrejoinMicEnabled(enabled);
        };
        updateMicEnabled();
        const observer = new MutationObserver(() => updateMicEnabled());
        observer.observe(wrapper, {
            subtree: true,
            attributes: true,
            attributeFilter: ["data-lk-enabled", "aria-pressed"],
        });
        return () => observer.disconnect();
    }, [shouldShowPrejoin]);

    // Ensure device menu (chevron) is always clickable when mic/camera is off - remove disabled if LiveKit sets it
    useEffect(() => {
        const wrapper = prejoinWrapperRef.current;
        if (!wrapper) return;
        const removeDisabled = () => {
            wrapper.querySelectorAll(
                ".lk-button-group-menu > .lk-button, .lk-button-group-menu [data-lk-device-menu-button]"
            ).forEach((el) => {
                if (el instanceof HTMLButtonElement) el.removeAttribute("disabled");
            });
        };
        removeDisabled();
        const observer = new MutationObserver(() => {
            observer.disconnect();
            try {
                removeDisabled();
            } finally {
                observer.observe(wrapper, {
                    subtree: true,
                    attributes: true,
                    attributeFilter: ["disabled"],
                });
            }
        });
        observer.observe(wrapper, {
            subtree: true,
            attributes: true,
            attributeFilter: ["disabled"],
        });
        return () => observer.disconnect();
    }, [activeUsersCount]);

    // Sanitize device labels in menus: strip hardware IDs like (3A71:F4B5)
    const sanitizeDeviceMenuLabels = useCallback(() => {
        const container = containerRef.current;
        if (!container) return;
        container.querySelectorAll(
            ".lk-device-menu .lk-button, .lk-media-device-select .lk-button, .lk-device-menu [role='menuitem'], .lk-device-menu [role='option']"
        ).forEach((el) => {
            if (!(el instanceof HTMLElement)) return;
            const raw = el.textContent || "";
            const sanitized = sanitizeDeviceLabel(raw);
            if (sanitized !== raw) {
                el.textContent = sanitized;
            }
        });
    }, []);

    useEffect(() => {
        sanitizeDeviceMenuLabels();
        const container = containerRef.current;
        if (!container) return;
        const observer = new MutationObserver(() => {
            sanitizeDeviceMenuLabels();
        });
        observer.observe(container, { childList: true, subtree: true, characterData: true });
        return () => observer.disconnect();
    }, [activeUsersCount, wantsToJoin, sanitizeDeviceMenuLabels]);

    const toggleFullscreen = useCallback(() => {
        setIsFullscreen((prev) => !prev);
    }, []);

    useEffect(() => {
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape" && isFullscreen) setIsFullscreen(false);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [isFullscreen]);

    const displayName = useMemo(() => {
        if (userName) return userName;
        try {
            const storedName = localStorage.getItem("anonymousUserName");
            if (storedName) return storedName;
        } catch { }
        return "User";
    }, [userName]);

    // Fetch a LiveKit token using the existing server endpoint
    useEffect(() => {
        const fetchToken = async () => {
            if (!sessionId) {
                setError("No session ID provided");
                setLoading(false);
                return;
            }
            if (!wantsToJoin) {
                return;
            }

            const roomName = `video-${sessionId}`;
            const participantName = displayName;

            const baseURL =
                process.env.NODE_ENV === "production" &&
                    (!process.env.REACT_APP_BACKEND_URL ||
                        process.env.REACT_APP_BACKEND_URL.includes("localhost"))
                    ? "https://interview-sandbox-production.up.railway.app"
                    : process.env.REACT_APP_BACKEND_URL || "http://localhost:8000";

            try {
                setLoading(true);
                setError(null);

                const response = await fetch(`${baseURL}/api/livekit/token`, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ roomName, participantName }),
                });

                if (!response.ok) {
                    let errorMessage = `HTTP ${response.status}`;
                    try {
                        const errorData = await response.json();
                        errorMessage = errorData.error || errorData.message || errorMessage;
                    } catch {
                        errorMessage = response.statusText || errorMessage;
                    }
                    throw new Error(errorMessage);
                }

                const data = await response.json();
                setToken(data.token);
                setUrl(data.url);
            } catch (err) {
                console.error("Error fetching LiveKit token for video chat:", err);
                setError(
                    err instanceof Error ? err.message : "Failed to connect to video chat"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchToken();
    }, [sessionId, displayName, wantsToJoin]);

    const handleConnected = useCallback(() => {
        console.log("VideoChatLiveKit: connected");
    }, []);

    const handleDisconnected = useCallback(() => {
        console.log("VideoChatLiveKit: disconnected");
        // Leave the room entirely: reset state so we show PreJoin again (or "Waiting for others")
        setWantsToJoin(false);
        setToken(null);
        setUrl(null);
    }, []);

    const handleError = useCallback((err: Error) => {
        console.error("VideoChatLiveKit: room error:", err);
        setError(err.message);
    }, []);

    const roomOptions = useMemo(
        () => ({
            publishDefaults: {
                videoSimulcastLayers: [],
            },
            audioCaptureDefaults: {
                autoGainControl: true,
                echoCancellation: true,
                noiseSuppression: true,
                deviceId: preJoinChoices?.audioDeviceId,
            },
            videoCaptureDefaults: {
                resolution: { width: 640, height: 480, frameRate: 24 },
                deviceId: preJoinChoices?.videoDeviceId,
            },
        }),
        [preJoinChoices]
    );

    if (!wantsToJoin) {
        if (activeUsersCount < 2) {
            return (
                <div
                    className={`videochat-lk-container videochat-lk-empty ${darkMode ? "dark" : ""}`}
                    style={{ flex: 1, minWidth: 0 }}
                    aria-hidden
                />
            );
        }

        return (
            <div ref={containerRef} className={`videochat-lk-container ${darkMode ? "dark" : ""}`} data-lk-theme="default">
                {!deviceDefaultsReady ? (
                    <div className="videochat-lk-loading">
                        <div className="videochat-lk-spinner" />
                        <p>Preparing camera...</p>
                    </div>
                ) : (
                    <div className="videochat-lk-prejoin-wrapper" ref={prejoinWrapperRef}>
                        <PreJoin
                            key={preferredVideoDeviceId ?? "default"}
                            onSubmit={(values) => {
                                setPreJoinChoices(values);
                                setWantsToJoin(true);
                            }}
                            defaults={{
                                audioEnabled: true,
                                videoEnabled: true,
                                username: displayName,
                                ...(preferredVideoDeviceId && { videoDeviceId: preferredVideoDeviceId }),
                            }}
                            persistUserChoices={false}
                            micLabel=""
                            camLabel=""
                            joinLabel=""
                        />
                        {prejoinVisualizerSlot &&
                            prejoinAudioTrack &&
                            createPortal(
                                <PreJoinVisualizer
                                    track={prejoinAudioTrack}
                                    micEnabled={prejoinMicEnabled}
                                />,
                                prejoinVisualizerSlot
                            )}
                    </div>
                )}
            </div>
        );
    }

    if (loading) {
        return (
            <div className={`videochat-lk-container ${darkMode ? "dark" : ""}`}>
                <div className="videochat-lk-loading">
                    <div className="videochat-lk-spinner" />
                    <p>Connecting to video...</p>
                </div>
            </div>
        );
    }

    if (error || !token || !url) {
        return (
            <div className={`videochat-lk-container ${darkMode ? "dark" : ""}`}>
                <div className="videochat-lk-error">
                    <p>{error || "Failed to get connection credentials"}</p>
                </div>
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className={`videochat-lk-container ${darkMode ? "dark" : ""} ${isFullscreen ? "videochat-lk-fullscreen" : ""}`}
            data-lk-theme="default"
        >
            <LiveKitRoom
                token={token}
                serverUrl={url}
                connect={true}
                audio={preJoinChoices?.audioEnabled ?? false}
                video={preJoinChoices?.videoEnabled ?? false}
                onConnected={handleConnected}
                onDisconnected={handleDisconnected}
                onError={handleError}
                options={roomOptions}
                className="videochat-lk-room"
            >
                <VideoChatInner
                    onPresenceUpdate={onPresenceUpdate}
                    isFullscreen={isFullscreen}
                    onFullscreenToggle={toggleFullscreen}
                />
            </LiveKitRoom>
        </div>
    );
};

export default VideoChatLiveKit;
